import { z } from 'zod';
import { Entitlement } from './plans.js';
import { Permission } from './tenancy.js';

/**
 * Server-signed, device-bound offline lease (spec §15).
 *
 * The lease is the ONLY thing that authorises offline use. It is signed with
 * Ed25519; the private key never ships. Expiry is the earlier of
 * (verifiedAt + 30 days) and the subscription's actual end date.
 */
export const LEASE_VERSION = 1;
export const LEASE_MAX_DAYS = 30;

export const LeaseClaims = z.object({
  v: z.literal(LEASE_VERSION),
  /** Lease id, for revocation lists and audit. */
  jti: z.uuid(),
  /** Key id, so keys can rotate without invalidating every lease at once. */
  kid: z.string().min(1),
  userId: z.uuid(),
  clinicId: z.uuid(),
  /** Binds the lease to one machine. See deviceFingerprint(). */
  deviceId: z.uuid(),
  deviceFingerprint: z.string().min(16),
  /** Membership + entitlement versions this lease was issued against. A server
   *  that has moved on refuses to renew and forces a re-download of scope. */
  membershipVersion: z.number().int().nonnegative(),
  entitlement: Entitlement,
  permissions: z.array(Permission),
  /** Unix seconds. */
  issuedAt: z.number().int().positive(),
  /** Unix seconds. Earlier of issuedAt+30d and subscription end. */
  expiresAt: z.number().int().positive(),
  /** Unix seconds: when the subscription itself ends, for honest messaging. */
  subscriptionEndsAt: z.number().int().positive().nullable(),
});
export type LeaseClaims = z.infer<typeof LeaseClaims>;

/** `base64url(json).base64url(ed25519 signature)` */
export const SignedLease = z.string().regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/);
export type SignedLease = z.infer<typeof SignedLease>;

export const LeaseFailure = z.enum([
  'malformed',
  'unknown_key',
  'bad_signature',
  'wrong_device',
  'wrong_user',
  'expired',
  'not_yet_valid',
  'clock_rollback',
  'revoked',
  'version_unsupported',
]);
export type LeaseFailure = z.infer<typeof LeaseFailure>;

export type LeaseVerdict =
  | { ok: true; claims: LeaseClaims; daysRemaining: number }
  | { ok: false; reason: LeaseFailure; detail?: string };

export const LeaseRequest = z.object({
  clinicId: z.uuid(),
  deviceId: z.uuid(),
  deviceFingerprint: z.string().min(16),
  deviceName: z.string().min(1).max(120),
  platform: z.string().min(1).max(60),
  appVersion: z.string().min(1).max(40),
});
export type LeaseRequest = z.infer<typeof LeaseRequest>;

export const LeaseResponse = z.object({
  lease: SignedLease,
  claims: LeaseClaims,
  /** Public keys the client should trust, so rotation is self-healing while online. */
  publicKeys: z.array(z.object({ kid: z.string(), publicKey: z.string() })),
});
export type LeaseResponse = z.infer<typeof LeaseResponse>;
